import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowClockwise, FloppyDisk, UploadSimple, Trash, Eye,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import BrandColourPalette from "@/components/BrandColourPalette";

/**
 * BrandingPanel — DSG-003 / DSG-004 Administration surface.
 *
 * Company Identity (Display / Legal / Short / Tagline) + five branded
 * assets (Primary Logo, Dark Logo, Square Logo, Favicon, Default
 * Application Icon). Base64-in-Mongo via existing branding.py routes.
 *
 * Auto-generated variants: uploading a Primary Logo derives Square Logo
 * and Application Icon defaults via a client-side canvas centre-crop.
 * Admins may override any generated asset with a custom upload.
 *
 * Live Preview: shows Login screen, Sidebar, and Header with UNSAVED
 * identity + staged assets so admins can validate before saving.
 *
 * Actions: Save · Cancel · Restore Defaults.
 */

const ASSET_SLOTS = [
  { key: "primary_logo",     label: "Primary Logo",             hint: "Horizontal logo used on Header, Sidebar, Sign-in, Access Denied." },
  { key: "dark_logo",        label: "Dark Logo",                hint: "Optional light-on-dark variant. Falls back to Primary." },
  { key: "square_logo",      label: "Square Logo",              hint: "Square badge / small avatar. Auto-generated from Primary if left blank." },
  { key: "favicon",          label: "Favicon",                  hint: "Browser tab icon (.ico / .png / .svg)." },
  { key: "application_icon", label: "Default Application Icon", hint: "Default icon for every registered application. Auto-generated from Primary if left blank." },
];

const ACCEPT_MIME = "image/png,image/jpeg,image/jpg,image/svg+xml,image/x-icon,image/vnd.microsoft.icon";
const MAX_BYTES = 2 * 1024 * 1024;

const DEFAULT_IDENTITY = {
  display_name: "Coolman Fuels",
  legal_name: "Coolman Fuels Ltd.",
  short_name: "Coolman",
  tagline: "",
};

// ---- Client-side auto-generation ------------------------------------------
// DSG-005 · When the Primary Logo changes, we can synthesise sensible
// defaults for every derived slot on the fly. Each generator loads the
// original file into an <img>, draws it onto a canvas with the shape
// that slot requires, and returns a PNG Blob ready for FormData upload.

// Load a File into an HTMLImageElement (returns null on decode failure).
function loadImage(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

// Centre-fit a logo onto a square canvas with a padded white background.
// Used for Square Logo, Application Icon, and Favicon variants.
async function generateSquareVariant(file, size = 512, padScale = 0.85) {
  const img = await loadImage(file);
  if (!img) return null;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, size, size);
  const scale = Math.min(size / img.width, size / img.height) * padScale;
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  return await new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/png", 0.92),
  );
}

// Generate a Dark Logo variant by rendering the primary onto a dark
// canvas + colour-inverting the pixels. Handles most brand marks that
// were designed for light backgrounds (black-on-white → white-on-dark).
// Preserves aspect ratio; output dimensions match the source.
async function generateDarkVariant(file) {
  const img = await loadImage(file);
  if (!img) return null;
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  // Dark surface behind the mark so transparency areas match the app's
  // dark-theme surface tone.
  ctx.fillStyle = "#0F1115";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imageData.data;
    // Invert RGB only where alpha is non-zero — leaves the dark
    // background untouched. Keeps original alpha so the mark stays crisp.
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] === 0) continue;
      d[i]     = 255 - d[i];
      d[i + 1] = 255 - d[i + 1];
      d[i + 2] = 255 - d[i + 2];
    }
    ctx.putImageData(imageData, 0, 0);
  } catch {
    // getImageData can fail if the source is cross-origin — fall back to
    // the un-inverted composite on the dark background so we still ship
    // *something* usable.
  }
  return await new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/png", 0.92),
  );
}

// Small 64×64 favicon derived from the same centre-fit routine so
// browsers get a crisp tab icon.
async function generateFaviconVariant(file) {
  return generateSquareVariant(file, 64, 0.9);
}

// DSG-005 · derived-asset generator registry. Adding a new slot = one entry.
const DERIVED_GENERATORS = {
  dark_logo:        { label: "Dark Logo",              generate: (f) => generateDarkVariant(f) },
  square_logo:      { label: "Square Logo",            generate: (f) => generateSquareVariant(f, 512) },
  application_icon: { label: "Default Application Icon", generate: (f) => generateSquareVariant(f, 512) },
  favicon:          { label: "Favicon",                generate: (f) => generateFaviconVariant(f) },
};
const DERIVED_KEYS = Object.keys(DERIVED_GENERATORS);

function blobToObjectUrl(blob) {
  return URL.createObjectURL(blob);
}

// Convert File/Blob → File so FormData carries a filename + type.
function ensureFile(source, filename, type) {
  if (source instanceof File) return source;
  return new File([source], filename, { type: type || source.type || "image/png" });
}

// ---------------------------------------------------------------------------

export default function BrandingPanel() {
  const { branding, host, refresh: refreshBranding } = useBranding();
  const [identity, setIdentity] = useState(DEFAULT_IDENTITY);
  const [initialIdentity, setInitialIdentity] = useState(DEFAULT_IDENTITY);
  // staged[assetKey] = { file, previewUrl, autoGenerated, remove } | null
  const [staged, setStaged] = useState({});
  const [savedAssets, setSavedAssets] = useState({}); // { primary_logo: "/api/branding/assets/primary_logo" | null }
  const [saving, setSaving] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("login"); // login | sidebar | header
  // DSG-005 · Derived-asset regeneration prompt state.
  //   file: the Primary Logo the admin just picked
  //   choices: { [derivedKey]: bool } — what to (re)generate on confirm
  const [regenPrompt, setRegenPrompt] = useState(null);

  // Load current state from branding context on mount / after save.
  useEffect(() => {
    const id = {
      display_name: branding?.identity?.display_name || DEFAULT_IDENTITY.display_name,
      legal_name:   branding?.identity?.legal_name   || DEFAULT_IDENTITY.legal_name,
      short_name:   branding?.identity?.short_name   || DEFAULT_IDENTITY.short_name,
      tagline:      branding?.identity?.tagline      || "",
    };
    setIdentity(id);
    setInitialIdentity(id);
    setSavedAssets(branding?.assets || {});
    // Discard any stale stage state whenever the underlying branding changes.
    setStaged({});
  }, [branding]);

  const identityDirty = useMemo(
    () => JSON.stringify(identity) !== JSON.stringify(initialIdentity),
    [identity, initialIdentity],
  );
  const stageDirty = Object.keys(staged).length > 0;
  const isDirty = identityDirty || stageDirty;

  const setField = (k) => (e) => setIdentity((s) => ({ ...s, [k]: e.target.value }));

  // ---- File pickers -------------------------------------------------------
  const onPickFile = async (slotKey, file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} exceeds the 2 MB limit.`);
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setStaged((s) => ({
      ...s,
      [slotKey]: { file, previewUrl, autoGenerated: false, remove: false },
    }));

    // DSG-005 · When Primary Logo changes, open the derived-asset
    // regeneration prompt. Preselect every derived slot EXCEPT ones
    // that already have a custom (saved OR staged) asset — those start
    // unchecked so we never silently overwrite an admin's custom
    // upload. The admin can still tick them to confirm overwrite.
    if (slotKey === "primary_logo") {
      const choices = {};
      for (const key of DERIVED_KEYS) {
        const hasStagedFile = !!staged[key]?.file;
        const hasSavedAsset = !!savedAssets[key];
        // Custom present → default OFF (require explicit confirmation).
        // No custom → default ON (safe to auto-fill).
        choices[key] = !(hasStagedFile || hasSavedAsset);
      }
      setRegenPrompt({ file, choices });
    }
  };

  // DSG-005 · Apply the derived-asset regeneration choice set. Called
  // when the admin confirms the prompt dialog. Runs each generator that
  // was ticked; skips ones that were unchecked.
  const applyRegeneration = async () => {
    if (!regenPrompt) return;
    const { file, choices } = regenPrompt;
    setRegenPrompt(null);
    const generated = [];
    for (const key of DERIVED_KEYS) {
      if (!choices[key]) continue;
      const gen = DERIVED_GENERATORS[key];
      const blob = await gen.generate(file);
      if (!blob) continue;
      const genFile = ensureFile(blob, `${key}.png`, "image/png");
      setStaged((s) => ({
        ...s,
        [key]: {
          file: genFile,
          previewUrl: blobToObjectUrl(blob),
          autoGenerated: true,
          remove: false,
        },
      }));
      generated.push(gen.label);
    }
    if (generated.length) {
      toast.success(
        `Auto-generated ${generated.length} derived asset${generated.length > 1 ? "s" : ""}. Save to apply.`,
      );
    } else {
      toast.message("No derived assets regenerated.");
    }
  };

  const skipRegeneration = () => setRegenPrompt(null);

  const onClearSlot = (slotKey) => {
    // Clearing a slot means: on save, DELETE the asset and revert to platform default.
    setStaged((s) => ({ ...s, [slotKey]: { remove: true, previewUrl: null, autoGenerated: false } }));
  };

  const onUndoSlot = (slotKey) => {
    setStaged((s) => {
      const next = { ...s };
      delete next[slotKey];
      return next;
    });
  };

  // ---- Preview resolvers --------------------------------------------------
  // What URL should the Live Preview render for a given asset slot?
  // Priority: staged.remove → null (fall back to Primary chain) · staged.file → object URL
  // · savedAssets[key] → server URL · null.
  const previewSrc = (slotKey) => {
    const s = staged[slotKey];
    if (s?.remove) return null;
    if (s?.previewUrl) return s.previewUrl;
    const url = savedAssets[slotKey];
    return url || null;
  };

  const primaryPreview = previewSrc("primary_logo");
  const squarePreview = previewSrc("square_logo") || primaryPreview;
  const appIconPreview = previewSrc("application_icon") || squarePreview;
  const faviconPreview = previewSrc("favicon");
  const darkPreview = previewSrc("dark_logo") || primaryPreview;
  // Provide a platform-default fallback when no primary is uploaded at all.
  const primaryPreviewFinal = primaryPreview || "/coolman-logo.png";

  // ---- Save ---------------------------------------------------------------
  const onSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      // 1) Identity (only PUT if dirty — the endpoint records a version snapshot).
      if (identityDirty) {
        await api.put("/admin/settings/branding/identity", identity);
      }
      // 2) Assets — POST uploads and DELETE removals.
      const entries = Object.entries(staged);
      for (const [key, stage] of entries) {
        if (stage.remove) {
          await api.delete(`/admin/settings/branding/assets/${key}`);
          continue;
        }
        if (stage.file) {
          const form = new FormData();
          form.append("file", stage.file);
          // Do not set Content-Type manually — axios needs to auto-populate
          // the multipart boundary. Override the client default explicitly.
          await api.post(
            `/admin/settings/branding/assets/${key}`,
            form,
            { headers: { "Content-Type": undefined } },
          );
        }
      }
      await refreshBranding();
      setStaged({});
      toast.success("Branding saved. Changes applied across the app.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    setIdentity(initialIdentity);
    setStaged({});
    toast.message("Unsaved changes discarded.");
  };

  const onRestoreDefaults = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/branding/restore-defaults");
      await refreshBranding();
      setStaged({});
      setRestoreOpen(false);
      toast.success("Branding restored to platform defaults.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Restore failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  return (
    <div data-testid="branding-panel" className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-charcoal">Company Branding</h2>
        <p className="text-sm text-ink-secondary">
          Company identity and brand assets are read live by every consumer — Sign-in,
          Sidebar, Header, Application Registry, and PDF/print headers. Changes take
          effect immediately after Save.
        </p>
      </div>

      {/* ------------- Identity ------------- */}
      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-charcoal">Company Identity</h3>
          {identityDirty && (
            <span className="text-xs font-medium text-status-warning-fg" data-testid="branding-identity-dirty">
              Unsaved changes
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="branding-display-name">Display Name</Label>
            <Input
              id="branding-display-name"
              data-testid="branding-display-name"
              value={identity.display_name}
              onChange={setField("display_name")}
            />
            <p className="mt-1 text-xs text-ink-muted">Shown on Sign-in, Header, and browser title.</p>
          </div>
          <div>
            <Label htmlFor="branding-legal-name">Legal Name</Label>
            <Input
              id="branding-legal-name"
              data-testid="branding-legal-name"
              value={identity.legal_name}
              onChange={setField("legal_name")}
            />
            <p className="mt-1 text-xs text-ink-muted">Used on invoices, PDFs, and legal footers.</p>
          </div>
          <div>
            <Label htmlFor="branding-short-name">Short Name</Label>
            <Input
              id="branding-short-name"
              data-testid="branding-short-name"
              value={identity.short_name}
              onChange={setField("short_name")}
            />
            <p className="mt-1 text-xs text-ink-muted">Used in compact areas (Sidebar footer, mobile nav).</p>
          </div>
          <div>
            <Label htmlFor="branding-tagline">Tagline (optional)</Label>
            <Input
              id="branding-tagline"
              data-testid="branding-tagline"
              value={identity.tagline || ""}
              onChange={setField("tagline")}
              placeholder="Internal Staff Portal"
            />
            <p className="mt-1 text-xs text-ink-muted">Shown under the Header title.</p>
          </div>
        </div>
      </Card>

      {/* ------------- Assets ------------- */}
      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-charcoal">Brand Assets</h3>
          <p className="text-xs text-ink-secondary">
            PNG / JPG / SVG / ICO · max 2 MB per file. Uploading a Primary Logo
            auto-generates default Square Logo and Application Icon variants
            — replace them with custom uploads if desired.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {ASSET_SLOTS.map((slot) => (
            <AssetSlot
              key={slot.key}
              slot={slot}
              stage={staged[slot.key]}
              savedUrl={savedAssets[slot.key]}
              previewUrl={previewSrc(slot.key)}
              onPick={(file) => onPickFile(slot.key, file)}
              onClear={() => onClearSlot(slot.key)}
              onUndo={() => onUndoSlot(slot.key)}
            />
          ))}
        </div>
      </Card>

      {/* ------------- Brand Colour Palette (DSG-006) ------------- */}
      <BrandColourPalette />

      {/* ------------- Live Preview ------------- */}
      <Card className="border-slate-200 p-5 shadow-sm" data-testid="branding-live-preview">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-charcoal">
              <Eye size={18} weight="bold" /> Live Preview
            </h3>
            <p className="text-xs text-ink-secondary">
              Simulated view of unsaved changes. Nothing is applied until you click Save.
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-md border border-divider bg-surface-alt p-1">
            {[
              { k: "login",   l: "Sign-in" },
              { k: "sidebar", l: "Sidebar" },
              { k: "header",  l: "Header" },
            ].map((m) => (
              <button
                key={m.k}
                type="button"
                data-testid={`branding-preview-mode-${m.k}`}
                onClick={() => setPreviewMode(m.k)}
                className={`rounded px-3 py-1 text-xs font-medium transition ${
                  previewMode === m.k
                    ? "bg-brand text-brand-foreground shadow-sm"
                    : "text-ink-secondary hover:bg-surface"
                }`}
              >
                {m.l}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-md border border-divider bg-surface-alt p-4">
          {previewMode === "login" && (
            <PreviewLogin identity={identity} host={host} primaryLogoUrl={primaryPreviewFinal} />
          )}
          {previewMode === "sidebar" && (
            <PreviewSidebar identity={identity} host={host} primaryLogoUrl={primaryPreviewFinal} appIconUrl={appIconPreview} />
          )}
          {previewMode === "header" && (
            <PreviewHeader identity={identity} host={host} primaryLogoUrl={primaryPreviewFinal} />
          )}
        </div>
      </Card>

      {/* ------------- Actions ------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4">
        <Button
          type="button"
          variant="outline"
          data-testid="branding-restore-defaults-open"
          onClick={() => setRestoreOpen(true)}
        >
          <ArrowClockwise size={16} weight="bold" className="mr-2" />
          Restore Defaults
        </Button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            data-testid="branding-cancel"
            disabled={!isDirty || saving}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="branding-save"
            disabled={!isDirty || saving}
            onClick={onSave}
          >
            <FloppyDisk size={16} weight="bold" className="mr-2" />
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Restore Defaults confirm dialog */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent data-testid="branding-restore-dialog">
          <DialogHeader>
            <DialogTitle>Restore platform defaults?</DialogTitle>
            <DialogDescription>
              This will clear every company identity override and remove every
              uploaded asset. The application will fall back to the platform
              defaults immediately. This action is recorded in the Branding
              version history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestoreOpen(false)} data-testid="branding-restore-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onRestoreDefaults}
              disabled={saving}
              data-testid="branding-restore-confirm"
            >
              {saving ? "Restoring…" : "Restore Defaults"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DSG-005 · Derived-asset regeneration prompt --------------------- */}
      <Dialog
        open={!!regenPrompt}
        onOpenChange={(v) => !v && skipRegeneration()}
      >
        <DialogContent data-testid="branding-regen-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Regenerate derived brand assets?</DialogTitle>
            <DialogDescription>
              You&apos;ve uploaded a new Primary Logo. We can automatically
              generate matching versions for the other slots below so
              your branding stays consistent everywhere. Slots holding a
              custom asset are unchecked by default — tick to overwrite.
            </DialogDescription>
          </DialogHeader>

          {regenPrompt && (
            <div className="space-y-2 py-1" data-testid="branding-regen-choices">
              {DERIVED_KEYS.map((key) => {
                const gen = DERIVED_GENERATORS[key];
                const hasStaged = !!staged[key]?.file;
                const hasSaved = !!savedAssets[key];
                const willOverwrite = hasStaged || hasSaved;
                const checked = !!regenPrompt.choices[key];
                return (
                  <label
                    key={key}
                    data-testid={`branding-regen-choice-${key}`}
                    className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 text-sm ${
                      checked
                        ? "border-brand/40 bg-brand/5"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setRegenPrompt((prev) => prev && ({
                        ...prev,
                        choices: { ...prev.choices, [key]: e.target.checked },
                      }))}
                      data-testid={`branding-regen-checkbox-${key}`}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-charcoal">{gen.label}</div>
                      {willOverwrite ? (
                        <div className="text-[11px] font-medium text-status-warning-fg" data-testid={`branding-regen-overwrite-${key}`}>
                          Will overwrite your custom {hasStaged ? "staged" : "saved"} {gen.label.toLowerCase()}. Uncheck to preserve it.
                        </div>
                      ) : (
                        <div className="text-[11px] text-ink-muted">
                          No custom asset on this slot — safe to generate.
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={skipRegeneration}
              data-testid="branding-regen-skip"
            >
              Skip
            </Button>
            <Button
              onClick={applyRegeneration}
              data-testid="branding-regen-confirm"
              disabled={
                !regenPrompt ||
                Object.values(regenPrompt.choices).every((v) => !v)
              }
            >
              Generate Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AssetSlot({ slot, stage, savedUrl, previewUrl, onPick, onClear, onUndo }) {
  const inputRef = useRef(null);
  const stagedForRemoval = stage?.remove;
  const stagedForUpload = !!stage?.file;
  const status = stagedForRemoval
    ? "Marked for removal"
    : stagedForUpload
      ? stage.autoGenerated ? "Auto-generated" : "Ready to upload"
      : savedUrl ? "Custom" : "Platform default";
  const statusTone = stagedForRemoval
    ? "text-status-error-fg"
    : stagedForUpload
      ? "text-status-info-fg"
      : savedUrl ? "text-status-success-fg" : "text-ink-muted";

  const displayUrl = stagedForRemoval ? null : previewUrl;
  return (
    <div
      data-testid={`branding-asset-${slot.key}`}
      className="flex flex-col rounded-md border border-divider bg-surface p-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-charcoal">{slot.label}</div>
        <span className={`text-[11px] font-medium ${statusTone}`} data-testid={`branding-asset-status-${slot.key}`}>{status}</span>
      </div>
      <div className="mt-2 flex h-24 items-center justify-center rounded border border-dashed border-divider bg-surface-alt">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={slot.label}
            className="max-h-20 max-w-full object-contain"
            data-testid={`branding-asset-preview-${slot.key}`}
          />
        ) : (
          <span className="text-[11px] text-ink-muted">No image</span>
        )}
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">{slot.hint}</p>
      <div className="mt-3 flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_MIME}
          className="hidden"
          data-testid={`branding-asset-file-${slot.key}`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            onPick(f);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`branding-asset-upload-${slot.key}`}
          onClick={() => inputRef.current?.click()}
        >
          <UploadSimple size={14} weight="bold" className="mr-1" />
          Upload
        </Button>
        {stage ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid={`branding-asset-undo-${slot.key}`}
            onClick={onUndo}
          >
            Undo
          </Button>
        ) : savedUrl ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid={`branding-asset-clear-${slot.key}`}
            onClick={onClear}
          >
            <Trash size={14} weight="bold" className="mr-1" />
            Revert
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// ---- Preview mocks --------------------------------------------------------

function PreviewLogin({ identity, host, primaryLogoUrl }) {
  const name = identity.display_name || "Coolman Fuels";
  const appTitle = host?.name || "Fuel Order Centre";
  const tag = identity.tagline || host?.subtitle || `Sign in with your ${name} account`;
  return (
    <div className="mx-auto max-w-sm border border-gray-200 bg-white p-6 text-center shadow-sm" data-testid="branding-preview-login">
      <img src={primaryLogoUrl} alt="" className="mx-auto h-16 w-auto" />
      <h4 className="mt-3 text-lg font-semibold text-gray-900" data-testid="branding-preview-login-name">{name}</h4>
      <div className="text-xs uppercase tracking-wider text-gray-500">{appTitle}</div>
      <p className="mt-2 text-xs text-gray-600" data-testid="branding-preview-login-tagline">{tag}</p>
      <div className="mt-4 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800">
        Sign in with Microsoft
      </div>
    </div>
  );
}

function PreviewSidebar({ identity, host, primaryLogoUrl, appIconUrl }) {
  const shortName = identity.short_name || "Coolman";
  const dept = host?.department || "Operations";
  return (
    <div className="mx-auto flex w-72 flex-col overflow-hidden rounded-md border border-divider bg-surface-alt" data-testid="branding-preview-sidebar">
      <div className="flex h-16 items-center justify-center border-b border-divider bg-surface px-4">
        <img src={primaryLogoUrl} alt="" className="h-10 w-auto object-contain" />
      </div>
      <div className="space-y-1 px-3 py-3">
        {["Dashboard", "New Order", "Tasks", "Fleet", "Customers"].map((label, i) => (
          <div
            key={label}
            className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs ${
              i === 0 ? "bg-brand text-brand-foreground" : "text-ink-secondary"
            }`}
          >
            {appIconUrl && i === 3 ? (
              <img src={appIconUrl} alt="" className="h-4 w-4 rounded object-cover" />
            ) : (
              <span className="h-4 w-4 rounded bg-divider" />
            )}
            {label}
          </div>
        ))}
      </div>
      <div className="border-t border-divider px-4 py-2 text-[10px] text-ink-muted" data-testid="branding-preview-sidebar-footer">
        {shortName} Fuels · {dept}
      </div>
    </div>
  );
}

function PreviewHeader({ identity, host, primaryLogoUrl }) {
  const name = identity.display_name || "Coolman Fuels";
  const appTitle = host?.name || "Fuel Order Centre";
  const tag = identity.tagline || host?.subtitle || `${name} · Internal Staff Portal`;
  return (
    <div className="flex items-center gap-4 rounded-md border border-divider bg-surface px-4 py-3" data-testid="branding-preview-header">
      <img src={primaryLogoUrl} alt="" className="h-10 w-auto object-contain" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-lg font-semibold text-charcoal" data-testid="branding-preview-header-name">
          {appTitle}
        </div>
        <div className="truncate text-xs text-ink-muted" data-testid="branding-preview-header-tagline">{tag}</div>
      </div>
      <div className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground">
        New Order
      </div>
    </div>
  );
}
